import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@app/shared';
import type { PermissionDef, RecordStatus } from '@app/shared';
import { tokens } from '../../app/theme';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { ApiError } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useGroup, useUpdateGroup } from './api';

const CATEGORIES: Array<{ name: string; items: PermissionDef[] }> = (() => {
  const map = new Map<string, PermissionDef[]>();
  for (const p of PERMISSIONS) map.set(p.category, [...(map.get(p.category) ?? []), p]);
  return [...map.entries()].map(([name, items]) => ({ name, items }));
})();

/** "Edit user group": name, status and the permission checklist, grouped by area. */
export function GroupEditPage() {
  const { id } = useParams<{ id: string }>();
  const notify = useNotify();
  const group = useGroup(id);
  const update = useUpdateGroup();

  const [name, setName] = useState('');
  const [status, setStatus] = useState<RecordStatus>('Active');
  const [codes, setCodes] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!group.data) return;
    setName(group.data.name);
    setStatus(group.data.status);
    setCodes(new Set(group.data.permissionCodes));
  }, [group.data]);

  const needle = search.trim().toLowerCase();
  // The reference's codes no screen uses yet are listed only while `permissions-all` is on.
  const allCodes = useFeatureReleased()('permissions-all');
  const listed = allCodes ? PERMISSIONS.length : PERMISSIONS.filter((p) => !p.referenceOnly).length;
  const visible = useMemo(
    () =>
      CATEGORIES.map((c) => ({
        ...c,
        items: c.items.filter(
          (p) =>
            (allCodes || !p.referenceOnly) &&
            (!needle || `${p.name} ${p.code} ${p.description}`.toLowerCase().includes(needle)),
        ),
      })).filter((c) => c.items.length > 0),
    [needle, allCodes],
  );

  if (group.isPending) return <LoadingState />;
  if (group.error) return <ErrorState error={group.error} onRetry={() => void group.refetch()} />;
  const isSystem = group.data.type === 'System';

  const toggle = (code: string, on: boolean) =>
    setCodes((prev) => {
      const next = new Set(prev);
      if (on) next.add(code);
      else next.delete(code);
      return next;
    });
  const toggleMany = (items: PermissionDef[], on: boolean) =>
    setCodes((prev) => {
      const next = new Set(prev);
      for (const p of items) {
        if (on) next.add(p.code);
        else next.delete(p.code);
      }
      return next;
    });

  const save = async () => {
    setNameError('');
    try {
      await update.mutateAsync({
        id: group.data.id,
        input: { ...(isSystem ? {} : { name }), status, permissionCodes: [...codes] },
      });
      notify.success('Permissions saved.');
    } catch (err) {
      const nameIssue =
        err instanceof ApiError ? err.details.find((d) => d.path === 'name') : undefined;
      if (nameIssue) setNameError(nameIssue.message);
      notify.error(err);
    }
  };

  return (
    <Box>
      <Button
        component={Link}
        to="/settings/users/groups"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1 }}
      >
        Back to user groups
      </Button>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" sx={{ mb: 1.5 }}>
          Edit user group
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Group name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSystem}
            error={Boolean(nameError)}
            helperText={nameError || (isSystem ? 'System groups cannot be renamed.' : ' ')}
            sx={{ maxWidth: 360 }}
          />
          <RadioGroup
            row
            value={status}
            onChange={(e) => setStatus(e.target.value as RecordStatus)}
            aria-label="Group status"
          >
            <FormControlLabel value="Active" control={<Radio size="small" />} label="Active" />
            <FormControlLabel value="Inactive" control={<Radio size="small" />} label="Inactive" />
          </RadioGroup>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={() => void save()} disabled={update.isPending}>
            Save permissions
          </Button>
        </Box>
        {status === 'Inactive' ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Members of an inactive group cannot sign in.
          </Alert>
        ) : null}
      </Paper>

      <TextField
        label="Search permissions"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5, maxWidth: 360 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {codes.size} of {listed} permissions selected. Codes match the reference product;
        permissions for screens that are not built yet have no effect.
      </Typography>

      {visible.map((category) => {
        const selected = category.items.filter((p) => codes.has(p.code)).length;
        const all = selected === category.items.length;
        return (
          <Paper key={category.name} variant="outlined" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 0.5,
                bgcolor: tokens.cardHeader,
              }}
            >
              <Typography variant="h3" sx={{ flexGrow: 1 }}>
                {category.name} Permissions
              </Typography>
              <FormControlLabel
                label="Select All"
                control={
                  <Checkbox
                    size="small"
                    checked={all}
                    indeterminate={selected > 0 && !all}
                    onChange={(e) => toggleMany(category.items, e.target.checked)}
                    inputProps={{ 'aria-label': `Select all ${category.name} permissions` }}
                  />
                }
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                px: 2,
                py: 1,
              }}
            >
              {category.items.map((p) => (
                <FormControlLabel
                  key={p.code}
                  sx={{ alignItems: 'flex-start', my: 0.25 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={codes.has(p.code)}
                      onChange={(e) => toggle(p.code, e.target.checked)}
                      sx={{ pt: 0.25 }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {p.name} ({p.code})
                        {p.referenceOnly ? (
                          <Chip label="Not used yet" size="small" sx={{ ml: 1, height: 18 }} />
                        ) : null}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {p.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
